<template>
    <div class="py-12">
        <h1
            class="text-2xl font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.edit") }}
        </h1>

        <form class="flex flex-col" @submit.prevent="updateAnnouncement">
            <TextInput
                v-model="v$.form.title.$model"
                class="my-6 w-52 sm:w-80"
                :label="$t('vue.title')"
                type="text"
                :placeholder="$t('vue.max_255_characters')"
                :error="getError('title')"
            />

            <TextInput
                v-model="dateComputed"
                :label="$t('vue.date')"
                type="datetime-local"
                class="mb-6 w-40"
            />

            <SelectInput
                v-model="state.form.type"
                class="mb-6 w-52 sm:w-80"
                :data="types"
                :label="$t('vue.type_of_announcement')"
                :placeholder="$t('vue.type')"
            />

            <EditorTinyMce
                v-model="v$.form.data.$model"
                api-key="y01dz3hyuzk77t9a3vl38gmcb3v62v3ft8pb8v2kpjuyyequ"
                :init="{
                    height: 500,
                    menubar: false,
                    plugins: [
                        'advlist autolink lists link image charmap print preview anchor',
                        'searchreplace visualblocks code fullscreen',
                        'insertdatetime media table paste code help wordcount',
                    ],
                    toolbar:
                        'undo redo | formatselect | bold italic backcolor | \
                            alignleft aligncenter alignright alignjustify | \
                            bullist numlist outdent indent | removeformat | help',
                }"
            />

            <div v-if="getError('data')">
                <div class="mt-2 text-sm text-custom-red-600">
                    {{ getError("data") }}
                </div>
            </div>

            <TextInput
                v-model="v$.form.button_text.$model"
                class="mt-6 w-52 sm:w-80"
                :label="$t('vue.button_text')"
                type="text"
                :placeholder="$t('vue.max_255_characters')"
                :error="getError('button_text')"
            />

            <TextInput
                v-model="v$.form.button_url.$model"
                class="mt-6 w-52 sm:w-80"
                :label="$t('vue.button_url')"
                type="text"
                :placeholder="$t('vue.max_2000_characters')"
                :error="getError('button_url')"
            />

            <div class="mt-4 flex flex-row">
                <AtomicButton
                    type="submit"
                    color="yellow"
                    size="sm"
                    :disabled="loading || v$.$invalid"
                >
                    {{ $t("vue.save_changes") }}
                </AtomicButton>

                <AtomicButton
                    class="ml-2"
                    size="sm"
                    color="red"
                    @click.prevent="deleteAnnouncement(announcement)"
                >
                    {{ $t("vue.delete") }}
                    <template #icon>
                        <TrashIcon
                            class="mr-2 h-6 w-6 transition duration-200 group-hover:text-gray-600"
                        />
                    </template>
                </AtomicButton>
            </div>
        </form>
    </div>
</template>

<script lang="ts" setup>
import EditorTinyMce from "@tinymce/tinymce-vue";
import { TrashIcon } from "@heroicons/vue/24/outline";
import useVuelidate from "@vuelidate/core";
import { required, maxLength } from "@vuelidate/validators";
import route from "ziggy-js";
import type { PropType } from "vue";
import type IAnnouncement from "@/types/Announcements/IAnnouncement";
import { router } from "@inertiajs/vue3";
import { create } from "@/composables/useNotifications";
import useConfirm from "@/composables/useConfirm";
import type IInputErrors from "@/types/Atomic/IInputErrors";

const props = defineProps({
    announcement: {
        type: Object as PropType<IAnnouncement>,
        required: true,
    },
});

const { show } = useConfirm();

const state = reactive({
    form: {
        title: props.announcement.title,
        publishedAt: props.announcement.published_at,
        type: props.announcement.type,
        data: props.announcement.data,
        button_text: props.announcement.button_text,
        button_url: props.announcement.button_url,
    },
});

const rules = {
    form: {
        title: {
            maxLength: maxLength(255),
        },
        data: {
            required,
            maxLength: maxLength(5000),
        },
        button_text: {
            maxLength: maxLength(255),
        },
        button_url: {
            maxLength: maxLength(2000),
        },
    },
};

const v$ = useVuelidate(rules, state);
const errors = ref<IInputErrors>({});
const loading = ref(false);
const types = [
    { title: $t("vue.news"), value: "news" },
    { title: $t("vue.report"), value: "report" },
    { title: $t("vue.warning"), value: "warning" },
    { title: $t("vue.critical"), value: "critical" },
];
const getError = (field: string) => {
    return (
        v$.value.form[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ""
    );
};

const dateComputed = computed({
    get: () => {
        return state.form.publishedAt
            ? new Date(state.form.publishedAt).toISOString().substring(0, 19)
            : "";
    },
    set: (newValue: string) => {
        state.form.publishedAt = newValue;
    },
});

const updateAnnouncement = () => {
    v$.value.$touch();
    if (v$.value.form.$error) {
        () => undefined;
    } else {
        loading.value = true;
        router.patch(
            route("kiosk.announcements.update", {
                announcement: props.announcement.id,
            }),
            state.form,
            {
                preserveScroll: true,
                onError: (err) => {
                    errors.value = err;
                },
                onSuccess: () => {
                    errors.value = {};
                },
                onFinish: () => {
                    loading.value = false;
                },
            },
        );
    }
};

const deleteAnnouncement = async (announcement: IAnnouncement) => {
    const confirmed = await show({
        header: $t("vue.delete_announcement_header"),
        message: $t("vue.delete_announcement_message"),
        type: "danger",
        confirm: $t("vue.delete"),
    });

    if (confirmed) {
        router.delete(
            route("kiosk.announcements.destroy", {
                announcement: announcement.id,
            }),
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: $t("vue.announcement_deleted"),
                        type: "success",
                    });
                },
                onError: () => {
                    create({
                        title: $t("vue.announcement_not_deleted"),
                        type: "error",
                    });
                },
            },
        );
    }
};
</script>

<style></style>
